/**
 * Appearance: the look of the whole app from one small seed.
 *
 * Colour reaches the screen through three sets of custom properties that
 * grew up separately — frappe-ui's semantic scales (`--surface-*`, `--ink-*`,
 * `--outline-*`), the console's own (`--c-*`) and the Genie shell's
 * (`--f-*`). A theme is not three hand-made value sets; it is fifteen or so
 * choices — a ground, a surface, an ink, an accent, the four state colours,
 * fonts, a radius, a type scale — from which every property is derived by
 * the same rules. That is what keeps a custom accent consistent from a
 * button to a chart, and what lets a preset be forty lines.
 *
 * Light and dark stay a separate switch: every theme carries a seed for
 * each, and the mode toggle keeps working under every theme.
 */

/* ---------------- colour arithmetic ---------------- */

export type RGB = [number, number, number];
export type Mode = "light" | "dark";

export function hexToRgb(hex: string): RGB {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHex([r, g, b]: RGB): string {
  return `#${[r, g, b].map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0")).join("")}`;
}

/** `a` moved `t` of the way towards `b` (0 = a, 1 = b). */
export function mix(a: RGB, b: RGB, t: number): RGB {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

function channel(c: number): number {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

/** Relative luminance, per WCAG. */
export function luminance([r, g, b]: RGB): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio between two colours, 1 to 21. */
export function contrast(a: RGB, b: RGB): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Space-separated channels, the form Tailwind's alpha modifiers need. */
function channels([r, g, b]: RGB): string {
  return `${Math.round(r)} ${Math.round(g)} ${Math.round(b)}`;
}

/* ---------------- the seed ---------------- */

/** One mode's worth of colour choices. */
export interface ModeSeed {
  /** The page behind everything. */
  ground: string;
  /** Cards, panels, the list. */
  surface: string;
  /** Body text at full strength. */
  ink: string;
  /** The accent: primary buttons, links, the selected state, the chart's magnitude hue. */
  accent: string;
  /** Text on the accent. */
  onAccent: string;
  good: string;
  warn: string;
  bad: string;
  info: string;
  /**
   * How far the neutral scales lean towards the accent's hue, 0 to 1. A
   * slate theme tints its greys a little; a pure white theme not at all.
   */
  tint?: number;
}

export type Radius = "sharp" | "soft" | "round";
export type Density = "compact" | "comfortable";

export interface ThemeSpec {
  id: string;
  name: string;
  tagline: string;
  /**
   * A dark rail over a light body — what "a dark theme" means to most
   * people. In light mode the rail is drawn from the dark seed; in dark
   * mode everything is dark.
   */
  chrome?: "dark";
  light: ModeSeed;
  dark: ModeSeed;
  /** Interface font, headings font, monospace — as CSS font-family lists. */
  fonts: { ui: string; heading: string; mono: string };
  radius: Radius;
  density: Density;
  /** Base type size in px, 13 to 16; frappe-ui's scale is built on 14. */
  baseSize: number;
}

/** What the operator changed on top of a preset. Every field optional. */
export interface AppearanceOverrides {
  accent?: string;
  fontUi?: string;
  fontHeading?: string;
  radius?: Radius;
  density?: Density;
  baseSize?: number;
}

export interface Appearance {
  theme: string;
  overrides: AppearanceOverrides;
}

/** Accents a person can pick without leaving the professional range. */
export const ACCENTS: { id: string; label: string; hex: string }[] = [
  { id: "blue", label: "Blue", hex: "#2490ef" },
  { id: "navy", label: "Navy", hex: "#0b5cab" },
  { id: "indigo", label: "Indigo", hex: "#4f46e5" },
  { id: "teal", label: "Teal", hex: "#0e8a7d" },
  { id: "green", label: "Green", hex: "#1f8a5b" },
  { id: "amber", label: "Amber", hex: "#c26a00" },
  { id: "red", label: "Red", hex: "#c53030" },
  { id: "plum", label: "Plum", hex: "#7c3aed" },
  { id: "gold", label: "Gold", hex: "#8a6d1f" },
  { id: "graphite", label: "Graphite", hex: "#1f2937" },
];

export const SIZE_CHOICES: { size: number; label: string }[] = [
  { size: 13, label: "Compact" },
  { size: 14, label: "Default" },
  { size: 15, label: "Comfortable" },
  { size: 16, label: "Large" },
];

export const RADIUS_CHOICES: { value: Radius; label: string }[] = [
  { value: "sharp", label: "Sharp" },
  { value: "soft", label: "Soft" },
  { value: "round", label: "Round" },
];

export const MIN_BASE_SIZE = 13;
export const MAX_BASE_SIZE = 16;

/* ---------------- fonts ---------------- */

export const FONT_CHOICES: { id: string; label: string; stack: string }[] = [
  { id: "inter", label: "Inter", stack: '"Inter", system-ui, sans-serif' },
  { id: "instrument", label: "Instrument Sans", stack: '"Instrument Sans", "Inter", system-ui, sans-serif' },
  { id: "plex", label: "IBM Plex Sans", stack: '"IBM Plex Sans", "Inter", system-ui, sans-serif' },
  { id: "grotesk", label: "Space Grotesk", stack: '"Space Grotesk", "Inter", system-ui, sans-serif' },
  { id: "roboto", label: "Roboto", stack: '"Roboto", "Inter", system-ui, sans-serif' },
  { id: "system", label: "System", stack: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' },
];
const MONO = '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace';

export function fontStack(id: string | undefined, fallback: string): string {
  return FONT_CHOICES.find((f) => f.id === id)?.stack ?? fallback;
}

/* ---------------- the presets ---------------- */

const NEUTRAL_GOOD = { good: "#1f8a5b", warn: "#b7791f", bad: "#c53030", info: "#2b6cb0" };
const NEUTRAL_GOOD_DARK = { good: "#4ade80", warn: "#fbbf24", bad: "#f87171", info: "#8ab4f8" };

export const PRESETS: ThemeSpec[] = [
  {
    id: "desk",
    name: "Desk",
    tagline: "The ERPNext desk as it is: neutral greys, blue accents, Inter.",
    light: { ground: "#ffffff", surface: "#ffffff", ink: "#171717", accent: "#2490ef", onAccent: "#ffffff", good: "#16a34a", warn: "#d97706", bad: "#dc2626", info: "#2563eb", tint: 0 },
    dark: { ground: "#121519", surface: "#171b20", ink: "#f0f2f4", accent: "#4f9cf9", onAccent: "#0b1220", ...NEUTRAL_GOOD_DARK, tint: 0.05 },
    fonts: { ui: fontStack("inter", ""), heading: fontStack("inter", ""), mono: MONO },
    radius: "soft",
    density: "comfortable",
    baseSize: 14,
  },
  {
    id: "slate",
    name: "Slate",
    tagline: "Cool blue-greys, a restrained blue, compact rows for people who live in lists.",
    light: { ground: "#ffffff", surface: "#f7f9fb", ink: "#141c26", accent: "#1f5fd0", onAccent: "#ffffff", ...NEUTRAL_GOOD, tint: 0.5 },
    dark: { ground: "#0b1118", surface: "#141c26", ink: "#dfe6ee", accent: "#5b9cf6", onAccent: "#08111c", ...NEUTRAL_GOOD_DARK, tint: 0.5 },
    fonts: { ui: fontStack("inter", ""), heading: fontStack("inter", ""), mono: MONO },
    radius: "sharp",
    density: "compact",
    baseSize: 13,
  },
  {
    id: "cloud",
    name: "Cloud",
    tagline: "A blue-tinted sky, white cards, a deep blue accent, rounder corners.",
    light: { ground: "#ffffff", surface: "#ffffff", ink: "#16325c", accent: "#0b5cab", onAccent: "#ffffff", good: "#2e844a", warn: "#a86403", bad: "#ba0517", info: "#0b5cab", tint: 0.7 },
    dark: { ground: "#061426", surface: "#0f2540", ink: "#e8f0fa", accent: "#57a3f2", onAccent: "#04101d", ...NEUTRAL_GOOD_DARK, tint: 0.7 },
    fonts: { ui: fontStack("inter", ""), heading: fontStack("inter", ""), mono: MONO },
    radius: "round",
    density: "comfortable",
    baseSize: 14,
  },
  {
    id: "fresh",
    name: "Fresh",
    tagline: "Mint-white with a teal accent and room to breathe.",
    light: { ground: "#ffffff", surface: "#ffffff", ink: "#0f3b3a", accent: "#0e8a7d", onAccent: "#ffffff", good: "#0e8a7d", warn: "#c77700", bad: "#d7263d", info: "#2c5cc5", tint: 0.5 },
    dark: { ground: "#071614", surface: "#0f2320", ink: "#e3efec", accent: "#3cc7b6", onAccent: "#06201c", ...NEUTRAL_GOOD_DARK, tint: 0.5 },
    fonts: { ui: fontStack("instrument", ""), heading: fontStack("instrument", ""), mono: MONO },
    radius: "round",
    density: "comfortable",
    baseSize: 15,
  },
  {
    id: "grid",
    name: "Grid",
    tagline: "Warm cream, tight tables, an amber accent.",
    light: { ground: "#ffffff", surface: "#fffdf8", ink: "#2a2118", accent: "#b45f06", onAccent: "#ffffff", good: "#11813a", warn: "#b45f06", bad: "#c62828", info: "#1e5fbf", tint: 0.5 },
    dark: { ground: "#191411", surface: "#231c17", ink: "#f1ebe4", accent: "#f2a950", onAccent: "#1a1200", ...NEUTRAL_GOOD_DARK, tint: 0.5 },
    fonts: { ui: fontStack("plex", ""), heading: fontStack("plex", ""), mono: MONO },
    radius: "sharp",
    density: "compact",
    baseSize: 13,
  },
  {
    id: "canvas",
    name: "Canvas",
    tagline: "Pure white, with blue, red, amber and green used sparingly. Roboto, pill corners.",
    light: { ground: "#ffffff", surface: "#ffffff", ink: "#202124", accent: "#1a73e8", onAccent: "#ffffff", good: "#188038", warn: "#e37400", bad: "#d93025", info: "#1a73e8", tint: 0 },
    dark: { ground: "#202124", surface: "#303134", ink: "#e8eaed", accent: "#8ab4f8", onAccent: "#0b1a33", good: "#81c995", warn: "#fdd663", bad: "#f28b82", info: "#8ab4f8", tint: 0 },
    fonts: { ui: fontStack("roboto", ""), heading: fontStack("roboto", ""), mono: MONO },
    radius: "round",
    density: "comfortable",
    baseSize: 14,
  },
  {
    id: "ledger",
    name: "Ledger",
    tagline: "Paper and ink: monochrome, hairline rules, tabular figures. For the numbers.",
    light: { ground: "#ffffff", surface: "#faf8f3", ink: "#111111", accent: "#1f2937", onAccent: "#ffffff", good: "#166534", warn: "#92400e", bad: "#991b1b", info: "#1e3a8a", tint: 0 },
    dark: { ground: "#0a0a0a", surface: "#161616", ink: "#f2f2f2", accent: "#d1d5db", onAccent: "#0b0b0b", ...NEUTRAL_GOOD_DARK, tint: 0 },
    fonts: { ui: fontStack("plex", ""), heading: fontStack("grotesk", ""), mono: MONO },
    radius: "sharp",
    density: "compact",
    baseSize: 13,
  },
  {
    id: "ink",
    name: "Ink",
    tagline: "High contrast, larger type, firmer borders.",
    light: { ground: "#ffffff", surface: "#ffffff", ink: "#000000", accent: "#0033a0", onAccent: "#ffffff", good: "#0f5e2e", warn: "#7a3e00", bad: "#8b0000", info: "#0033a0", tint: 0 },
    dark: { ground: "#000000", surface: "#0e0e0e", ink: "#ffffff", accent: "#ffd166", onAccent: "#000000", good: "#7ee2a8", warn: "#ffd166", bad: "#ff8a80", info: "#9ec5ff", tint: 0 },
    fonts: { ui: fontStack("inter", ""), heading: fontStack("inter", ""), mono: MONO },
    radius: "soft",
    density: "comfortable",
    baseSize: 16,
  },

  /* Dark rail, light body: the dark seed draws the rail; the light seed the pages. */
  {
    id: "graphite",
    name: "Graphite",
    tagline: "Charcoal with a cyan accent — the console's own dark.",
    chrome: "dark",
    light: { ground: "#ffffff", surface: "#ffffff", ink: "#0d1a21", accent: "#0c748f", onAccent: "#ffffff", good: "#147a52", warn: "#92590c", bad: "#b22a24", info: "#1557b0", tint: 0.1 },
    dark: { ground: "#05070c", surface: "#111926", ink: "#e2e8f0", accent: "#38bdf8", onAccent: "#04111c", good: "#34d399", warn: "#fbbf24", bad: "#f87171", info: "#8ab4f8", tint: 0.35 },
    fonts: { ui: fontStack("inter", ""), heading: fontStack("grotesk", ""), mono: MONO },
    radius: "soft",
    density: "comfortable",
    baseSize: 14,
  },
  {
    id: "midnight",
    name: "Midnight",
    tagline: "Deep navy with a muted gold accent.",
    chrome: "dark",
    light: { ground: "#ffffff", surface: "#ffffff", ink: "#141a2e", accent: "#8a6d1f", onAccent: "#ffffff", good: "#1f7a4d", warn: "#a15c00", bad: "#b3261e", info: "#2f4fa8", tint: 0.3 },
    dark: { ground: "#070c1f", surface: "#101a3a", ink: "#e9ecf6", accent: "#d9b85c", onAccent: "#1a1300", good: "#5ad39a", warn: "#f5c451", bad: "#f38b8b", info: "#93aef7", tint: 0.6 },
    fonts: { ui: fontStack("inter", ""), heading: fontStack("inter", ""), mono: MONO },
    radius: "soft",
    density: "comfortable",
    baseSize: 14,
  },
  {
    id: "obsidian",
    name: "Obsidian",
    tagline: "True black, violet accent, glass-thin borders.",
    chrome: "dark",
    light: { ground: "#ffffff", surface: "#ffffff", ink: "#1a1428", accent: "#6d28d9", onAccent: "#ffffff", good: "#1f8a5b", warn: "#b7791f", bad: "#c53030", info: "#4f46e5", tint: 0.2 },
    dark: { ground: "#000000", surface: "#0f0d14", ink: "#ece9f4", accent: "#a78bfa", onAccent: "#12071f", good: "#4ade80", warn: "#fbbf24", bad: "#fb7185", info: "#818cf8", tint: 0.35 },
    fonts: { ui: fontStack("inter", ""), heading: fontStack("grotesk", ""), mono: MONO },
    radius: "round",
    density: "comfortable",
    baseSize: 14,
  },
  {
    id: "forest",
    name: "Forest",
    tagline: "Deep green with a mint accent, warm off-white type.",
    chrome: "dark",
    light: { ground: "#ffffff", surface: "#ffffff", ink: "#12261a", accent: "#1f7a4d", onAccent: "#ffffff", good: "#1f7a4d", warn: "#a15c00", bad: "#b3261e", info: "#2b6cb0", tint: 0.3 },
    dark: { ground: "#07130d", surface: "#0f2016", ink: "#ece9dc", accent: "#5ee0a6", onAccent: "#04180d", good: "#5ee0a6", warn: "#f5c451", bad: "#f38b8b", info: "#8ab4f8", tint: 0.6 },
    fonts: { ui: fontStack("instrument", ""), heading: fontStack("instrument", ""), mono: MONO },
    radius: "soft",
    density: "comfortable",
    baseSize: 14,
  },
  {
    id: "ember",
    name: "Ember",
    tagline: "Warm charcoal with an amber-orange accent.",
    chrome: "dark",
    light: { ground: "#ffffff", surface: "#ffffff", ink: "#26190f", accent: "#c2410c", onAccent: "#ffffff", good: "#1f8a5b", warn: "#b45309", bad: "#b91c1c", info: "#2b6cb0", tint: 0.3 },
    dark: { ground: "#141010", surface: "#1f1917", ink: "#f3ede6", accent: "#fb923c", onAccent: "#1f0d00", good: "#4ade80", warn: "#fbbf24", bad: "#f87171", info: "#93c5fd", tint: 0.5 },
    fonts: { ui: fontStack("plex", ""), heading: fontStack("plex", ""), mono: MONO },
    radius: "soft",
    density: "compact",
    baseSize: 14,
  },
  {
    id: "ocean",
    name: "Ocean",
    tagline: "Deep teal-navy with an aqua accent.",
    chrome: "dark",
    light: { ground: "#ffffff", surface: "#ffffff", ink: "#0e2a36", accent: "#0e7490", onAccent: "#ffffff", good: "#0f766e", warn: "#a16207", bad: "#b91c1c", info: "#0e7490", tint: 0.4 },
    dark: { ground: "#03141c", surface: "#0a2330", ink: "#e4f1f5", accent: "#22d3ee", onAccent: "#03171c", good: "#34d399", warn: "#fbbf24", bad: "#fb7185", info: "#67e8f9", tint: 0.7 },
    fonts: { ui: fontStack("inter", ""), heading: fontStack("inter", ""), mono: MONO },
    radius: "round",
    density: "comfortable",
    baseSize: 14,
  },
  {
    id: "plum",
    name: "Plum",
    tagline: "Aubergine with a rose-gold accent.",
    chrome: "dark",
    light: { ground: "#ffffff", surface: "#ffffff", ink: "#2a1424", accent: "#9d174d", onAccent: "#ffffff", good: "#1f8a5b", warn: "#b7791f", bad: "#c53030", info: "#6d28d9", tint: 0.3 },
    dark: { ground: "#160a13", surface: "#24121f", ink: "#f4e9f0", accent: "#e8a598", onAccent: "#2a0d12", good: "#6ee7b7", warn: "#fcd34d", bad: "#fda4af", info: "#c4b5fd", tint: 0.5 },
    fonts: { ui: fontStack("instrument", ""), heading: fontStack("grotesk", ""), mono: MONO },
    radius: "round",
    density: "comfortable",
    baseSize: 14,
  },
  /* ---- ten more, each with a reference in mind and a reason to exist ---- */
  {
    id: "porcelain",
    name: "Porcelain",
    tagline: "White, hairline grey, one blue. The quiet end of the scale.",
    light: { ground: "#ffffff", surface: "#f7f7f8", ink: "#1d1d1f", accent: "#0066cc", onAccent: "#ffffff", good: "#1f7a4d", warn: "#a15c00", bad: "#b3261e", info: "#0066cc", tint: 0.1 },
    dark: { ground: "#000000", surface: "#1c1c1e", ink: "#f5f5f7", accent: "#5aa9ff", onAccent: "#00172e", good: "#5ad39a", warn: "#f5c451", bad: "#f38b8b", info: "#8ec5ff", tint: 0.1 },
    fonts: { ui: fontStack("inter", ""), heading: fontStack("inter", ""), mono: MONO },
    radius: "round",
    density: "comfortable",
    baseSize: 14,
  },
  {
    id: "linen",
    name: "Linen",
    tagline: "Warm white paper and a violet accent, set in a humanist face.",
    light: { ground: "#ffffff", surface: "#fffdf9", ink: "#1f1d1a", accent: "#5b52e0", onAccent: "#ffffff", good: "#1f7a4d", warn: "#9a5b00", bad: "#b3261e", info: "#3b5bdb", tint: 0.15 },
    dark: { ground: "#0f0e0c", surface: "#1a1917", ink: "#efe9df", accent: "#aaa5ff", onAccent: "#100f2a", good: "#5ad39a", warn: "#f5c451", bad: "#f38b8b", info: "#93aef7", tint: 0.2 },
    fonts: { ui: fontStack("instrument", ""), heading: fontStack("instrument", ""), mono: MONO },
    radius: "soft",
    density: "comfortable",
    baseSize: 14,
  },
  {
    id: "carbon",
    name: "Carbon",
    tagline: "Black rail, white body, black accent. Grotesk, sharp corners.",
    chrome: "dark",
    light: { ground: "#ffffff", surface: "#fafafa", ink: "#111111", accent: "#111111", onAccent: "#ffffff", good: "#166534", warn: "#92400e", bad: "#991b1b", info: "#1e3a8a", tint: 0 },
    dark: { ground: "#0a0a0a", surface: "#141414", ink: "#ededed", accent: "#f5f5f5", onAccent: "#0a0a0a", good: "#4ade80", warn: "#fbbf24", bad: "#fb7185", info: "#7dd3fc", tint: 0 },
    fonts: { ui: fontStack("grotesk", ""), heading: fontStack("grotesk", ""), mono: MONO },
    radius: "sharp",
    density: "comfortable",
    baseSize: 14,
  },
  {
    id: "nordic",
    name: "Nordic",
    tagline: "Cool blue-grey neutrals, a calm steel accent, a dark rail.",
    chrome: "dark",
    light: { ground: "#ffffff", surface: "#fbfcfd", ink: "#1b2733", accent: "#2b6cb0", onAccent: "#ffffff", good: "#2f855a", warn: "#b7791f", bad: "#c53030", info: "#2b6cb0", tint: 0.4 },
    dark: { ground: "#0b1117", surface: "#131b23", ink: "#e6edf3", accent: "#79b8ff", onAccent: "#05101c", good: "#5ad39a", warn: "#f5c451", bad: "#f38b8b", info: "#79b8ff", tint: 0.5 },
    fonts: { ui: fontStack("inter", ""), heading: fontStack("inter", ""), mono: MONO },
    radius: "soft",
    density: "comfortable",
    baseSize: 14,
  },
  {
    id: "sandstone",
    name: "Sandstone",
    tagline: "Warm taupe neutrals with a terracotta accent.",
    light: { ground: "#ffffff", surface: "#fdfbf7", ink: "#2a2420", accent: "#b5442d", onAccent: "#ffffff", good: "#2f7a4f", warn: "#9a5b00", bad: "#b3261e", info: "#2f5fa8", tint: 0.2 },
    dark: { ground: "#15110e", surface: "#201a16", ink: "#f1e9e0", accent: "#f0906f", onAccent: "#2a0e05", good: "#6ad39a", warn: "#f5c451", bad: "#f38b8b", info: "#93aef7", tint: 0.25 },
    fonts: { ui: fontStack("instrument", ""), heading: fontStack("instrument", ""), mono: MONO },
    radius: "round",
    density: "comfortable",
    baseSize: 14,
  },
  {
    id: "aurora",
    name: "Aurora",
    tagline: "Deep teal rail, mint accent, a grotesk for headings.",
    chrome: "dark",
    light: { ground: "#ffffff", surface: "#ffffff", ink: "#0f2a2a", accent: "#0f766e", onAccent: "#ffffff", good: "#0f766e", warn: "#a15c00", bad: "#b3261e", info: "#2b6cb0", tint: 0.3 },
    dark: { ground: "#06110f", surface: "#0d1c19", ink: "#e6f2ef", accent: "#2dd4bf", onAccent: "#032b26", good: "#2dd4bf", warn: "#f5c451", bad: "#f38b8b", info: "#8ab4f8", tint: 0.5 },
    fonts: { ui: fontStack("inter", ""), heading: fontStack("grotesk", ""), mono: MONO },
    radius: "round",
    density: "comfortable",
    baseSize: 14,
  },
  {
    id: "royal",
    name: "Royal",
    tagline: "Indigo rail with an antique gold accent. Plex throughout.",
    chrome: "dark",
    light: { ground: "#ffffff", surface: "#ffffff", ink: "#1a1a3a", accent: "#3730a3", onAccent: "#ffffff", good: "#1f7a4d", warn: "#a15c00", bad: "#b3261e", info: "#3730a3", tint: 0.3 },
    dark: { ground: "#0a0a24", surface: "#151540", ink: "#ecebfa", accent: "#d4c26a", onAccent: "#1a1500", good: "#5ad39a", warn: "#f5c451", bad: "#f38b8b", info: "#a5b4fc", tint: 0.5 },
    fonts: { ui: fontStack("plex", ""), heading: fontStack("plex", ""), mono: MONO },
    radius: "soft",
    density: "comfortable",
    baseSize: 14,
  },
  {
    id: "mono",
    name: "Mono",
    tagline: "Editorial monochrome: black on white, no colour but the states.",
    light: { ground: "#ffffff", surface: "#ffffff", ink: "#000000", accent: "#111111", onAccent: "#ffffff", good: "#14532d", warn: "#78350f", bad: "#7f1d1d", info: "#1e3a8a", tint: 0 },
    dark: { ground: "#000000", surface: "#0b0b0b", ink: "#ffffff", accent: "#f5f5f5", onAccent: "#000000", good: "#86efac", warn: "#fde68a", bad: "#fca5a5", info: "#bfdbfe", tint: 0 },
    fonts: { ui: fontStack("instrument", ""), heading: fontStack("instrument", ""), mono: MONO },
    radius: "sharp",
    density: "comfortable",
    baseSize: 14,
  },
  {
    id: "coral",
    name: "Coral",
    tagline: "Neutral greys with a single warm accent, round corners.",
    light: { ground: "#ffffff", surface: "#ffffff", ink: "#1f2328", accent: "#c8285f", onAccent: "#ffffff", good: "#1f7a4d", warn: "#a15c00", bad: "#b3261e", info: "#2f5fa8", tint: 0.1 },
    dark: { ground: "#0e0f12", surface: "#17181c", ink: "#ecedf0", accent: "#ff7aa8", onAccent: "#2b0412", good: "#5ad39a", warn: "#f5c451", bad: "#f38b8b", info: "#93aef7", tint: 0.15 },
    fonts: { ui: fontStack("inter", ""), heading: fontStack("grotesk", ""), mono: MONO },
    radius: "round",
    density: "comfortable",
    baseSize: 14,
  },
  {
    id: "harbor",
    name: "Harbor",
    tagline: "Navy rail, slate neutrals, a clear blue accent for the enterprise desk.",
    chrome: "dark",
    light: { ground: "#ffffff", surface: "#ffffff", ink: "#1e293b", accent: "#1d4ed8", onAccent: "#ffffff", good: "#15803d", warn: "#b45309", bad: "#b91c1c", info: "#1d4ed8", tint: 0.5 },
    dark: { ground: "#0b1220", surface: "#111a2e", ink: "#e2e8f0", accent: "#60a5fa", onAccent: "#06162e", good: "#5ad39a", warn: "#f5c451", bad: "#f38b8b", info: "#93c5fd", tint: 0.6 },
    fonts: { ui: fontStack("inter", ""), heading: fontStack("inter", ""), mono: MONO },
    radius: "soft",
    density: "comfortable",
    baseSize: 14,
  },
];

export const DEFAULT_THEME = "desk";

export function presetById(id: string): ThemeSpec {
  return PRESETS.find((p) => p.id === id) ?? PRESETS[0];
}

/* ---------------- the derivation ---------------- */

/** The neutral scales one seed yields, by the rules a mode wants. */
function scalesOf(seed: ModeSeed, dark: boolean, accentOverride?: string) {
  const ground = hexToRgb(seed.ground);
  const surface = hexToRgb(seed.surface);
  const ink = hexToRgb(seed.ink);
  const accent = hexToRgb(accentOverride ?? seed.accent);
  const tint = seed.tint ?? 0;
  const tinted = (c: RGB) => mix(c, accent, tint * 0.14);
  const step = (t: number): RGB => tinted(mix(surface, ink, t));
  const grounded = (t: number): RGB => tinted(mix(ground, ink, t));
  const inkAt = (t: number): RGB => mix(surface, ink, t);
  // The steps that carry text are pushed towards the ink until they clear
  // the contrast a reader needs — a softer ink gets firmer greys, by rule.
  const readable = (t: number, ratio: number): RGB => {
    let at = t;
    while (at < 1 && contrast(inkAt(at), surface) < ratio) at += 0.02;
    return inkAt(Math.min(1, at));
  };
  return {
    ground,
    surface,
    ink,
    accent,
    good: hexToRgb(seed.good),
    warn: hexToRgb(seed.warn),
    bad: hexToRgb(seed.bad),
    info: hexToRgb(seed.info),
    inks: {
      9: inkAt(1),
      8: readable(dark ? 0.9 : 0.88, 6.5),
      7: readable(dark ? 0.8 : 0.76, 5.5),
      // Ink 6 also sits on the second grey (a grey pill), so it clears more on white.
      6: readable(dark ? 0.7 : 0.66, 5.6),
      5: readable(dark ? 0.58 : 0.6, 4.6),
      4: inkAt(dark ? 0.46 : 0.45),
      3: inkAt(dark ? 0.34 : 0.3),
      2: inkAt(dark ? 0.22 : 0.18),
      1: inkAt(dark ? 0.14 : 0.1),
    },
    surfaces: {
      // Light pages are white; the first grey step is a hair off it, so a
      // table head or a menu bar still reads as a surface of its own.
      1: dark ? step(0.05) : grounded(0.025),
      2: dark ? step(0.09) : step(0.04),
      3: dark ? step(0.14) : step(0.08),
      4: dark ? step(0.2) : step(0.14),
      5: step(dark ? 0.86 : 0.7),
      6: step(0.8),
      7: step(dark ? 0.92 : 0.9),
    },
    outlines: {
      1: step(dark ? 0.12 : 0.08),
      2: step(dark ? 0.18 : 0.13),
      3: step(dark ? 0.28 : 0.22),
      4: step(0.5),
      5: step(0.6),
    },
  };
}

type Scales = ReturnType<typeof scalesOf>;

const alpha = (c: RGB, a: number) => `rgba(${Math.round(c[0])}, ${Math.round(c[1])}, ${Math.round(c[2])}, ${a})`;

/** White where white reads on this fill, else a near-black. */
function labelOn(fill: RGB): RGB {
  return contrast(fill, [255, 255, 255]) >= 3 ? [255, 255, 255] : [11, 18, 32];
}

/**
 * A colour as text on a ground: moved towards the ink (light mode) or the
 * surface's opposite (dark mode) until it clears AA on that ground. The
 * pill's text keeps the pill's hue, and reads.
 */
function readableOn(c: RGB, ground: RGB, towards: RGB, ratio = 4.5): RGB {
  let t = 0;
  let out = c;
  while (t < 1 && contrast(out, ground) < ratio) {
    t += 0.04;
    out = mix(c, towards, t);
  }
  return out;
}

/**
 * The shell's semantic tokens from one set of scales: what the page body
 * reads, and — from the chrome's scales — what the rail reads.
 */
function shellTokens(s: Scales, dark: boolean): Record<string, string> {
  const rgb = rgbToHex;
  const { ground, surface, ink, accent, good, warn, bad, inks, surfaces, outlines } = s;
  const softOf = (c: RGB) => (dark ? alpha(c, 0.16) : rgb(mix(surface, c, 0.12)));
  const inkOf = (c: RGB) => (dark ? rgb(mix(c, ink, 0.35)) : rgb(mix(c, ink, 0.45)));
  const railBg = dark ? ground : surfaces[1];
  const railInk = (t: number, ratio: number): RGB => {
    let at = t;
    while (at < 1 && contrast(mix(railBg, ink, at), railBg) < ratio) at += 0.02;
    return mix(railBg, ink, Math.min(1, at));
  };
  return {
    ink: rgb(ink),
    "ink-2": alpha(ink, dark ? 0.74 : 0.72),
    "ink-3": alpha(ink, dark ? 0.52 : 0.5),
    "ink-4": alpha(ink, 0.36),
    hair: dark ? "rgba(255, 255, 255, 0.13)" : alpha(ink, 0.13),
    "hair-soft": dark ? "rgba(255, 255, 255, 0.07)" : alpha(ink, 0.07),
    page: rgb(ground),
    card: rgb(surface),
    blue: rgb(accent),
    "blue-ink": rgb(dark ? mix(accent, ink, 0.3) : mix(accent, ink, 0.15)),
    "blue-soft": alpha(accent, dark ? 0.16 : 0.09),
    "home-base": rgb(dark ? ground : surface),
    ok: rgb(good),
    warn: rgb(warn),
    alarm: rgb(bad),
    raise: dark ? "rgba(255, 255, 255, 0.045)" : rgb(surfaces[1]),
    "raise-2": dark ? "rgba(255, 255, 255, 0.075)" : rgb(surfaces[2]),
    sunken: rgb(dark ? mix(ground, [0, 0, 0], 0.3) : mix(ink, surface, 0.06)),
    "sunken-ink": rgb(dark ? inks[8] : surfaces[2]),
    "good-soft": softOf(good),
    "good-ink": inkOf(good),
    "caution-soft": softOf(warn),
    "caution-line": dark ? alpha(warn, 0.4) : rgb(mix(surface, warn, 0.4)),
    "caution-ink": inkOf(warn),
    "danger-soft": softOf(bad),
    "danger-ink": inkOf(bad),
    "hero-bg": rgb(dark ? mix(ground, [0, 0, 0], 0.5) : mix(ink, [0, 0, 0], 0.1)),
    "hero-ink": rgb(dark ? inks[9] : surface),
    "badge-bg": dark ? "rgba(255, 255, 255, 0.08)" : rgb(surfaces[3]),
    "badge-ink": rgb(inks[7]),
    // Text on the accent itself — a button, a counter — white or near-black by contrast.
    "on-accent": rgb(labelOn(accent)),
    // The rail's own palette (the `--f-*` tokens). Its inks are solved
    // against the rail's ground, which is a shade off the page's.
    "f-gray-50": rgb(surfaces[1]),
    "f-gray-100": rgb(surfaces[2]),
    "f-gray-200": rgb(surfaces[3]),
    "f-gray-300": rgb(surfaces[4]),
    "f-ink-5": rgb(railInk(0.45, 3)),
    "f-ink-6": rgb(railInk(dark ? 0.58 : 0.6, 4.6)),
    "f-ink-7": rgb(railInk(dark ? 0.8 : 0.76, 5.5)),
    "f-ink-8": rgb(inks[9]),
    "f-bg": rgb(railBg),
    "f-border": rgb(outlines[1]),
    "f-hover": rgb(surfaces[3]),
    "f-ink-8-strong": rgb(inks[9]),
    accent: rgb(accent),
  };
}

/** Every custom property the app reads, from one seed. */
export function deriveVariables(spec: ThemeSpec, mode: Mode, overrides: AppearanceOverrides = {}): Record<string, string> {
  const seed = spec[mode];
  const dark = mode === "dark";
  const main = scalesOf(seed, dark, overrides.accent);
  const { ground, surface, ink, accent, good, warn, bad, info, inks, surfaces, outlines } = main;
  // A chosen accent brings its own label colour: white where white reads,
  // else a near-black — the preset's choice only holds for the preset's accent.
  const onAccent = overrides.accent
    ? contrast(accent, [255, 255, 255]) >= 3
      ? ([255, 255, 255] as RGB)
      : hexToRgb("#0b1220")
    : hexToRgb(seed.onAccent);

  /**
   * The chrome — the rail — may be dark while the body is light: "a dark
   * theme" to most people means a dark sidebar over white pages, not a dark
   * page. Such a theme draws its rail from its dark seed in light mode; in
   * dark mode the rail and the body are one.
   */
  const chrome = spec.chrome === "dark" && !dark ? scalesOf(spec.dark, true, overrides.accent) : main;
  const chromeDark = spec.chrome === "dark" || dark;

  const wash = (c: RGB, t = dark ? 0.22 : 0.14): RGB => mix(surface, c, t);
  const strong = (c: RGB): RGB => (dark ? mix(c, ink, 0.25) : mix(c, ink, 0.3));
  const rgb = rgbToHex;
  const out: Record<string, string> = {};

  /* frappe-ui semantic scales, under the names its compiled utilities read: --surface-*, --ink-*, --outline-* */
  out["--surface-white"] = rgb(surface);
  out["--surface-cards"] = rgb(surface);
  out["--surface-modal"] = rgb(dark ? surfaces[2] : surface);
  out["--surface-menu-bar"] = rgb(surfaces[1]);
  out["--surface-selected"] = rgb(dark ? surfaces[3] : surface);
  for (const [n, c] of Object.entries(surfaces)) out[`--surface-gray-${n}`] = rgb(c);
  for (const [n, c] of Object.entries(inks)) out[`--ink-gray-${n}`] = rgb(c);
  // What sits on the darkest neutral (gray-7) and on solid fills.
  out["--ink-white"] = rgb(labelOn(surfaces[7]));
  for (const [n, c] of Object.entries(outlines)) out[`--outline-gray-${n}`] = rgb(c);
  out["--outline-gray-modals"] = rgb(outlines[2]);
  out["--outline-white"] = rgb(dark ? surfaces[3] : surface);
  /*
   * The state colours, in the roles this frappe-ui build gives them: the
   * numbered surfaces are washes (blue-2, red-2, amber-1/2, green-2) with
   * green-3 and gray-7 the only solid fills; a pill's text is the ink its
   * Badge pairs with that wash. Each text is solved to read on the wash it
   * sits on, so a mint accent gets a deep mint label, not a mint one.
   */
  // Text moves towards whichever of the ink and the page ground stands
  // further from the wash — on a strong wash in dark mode that is the dark
  // ground, not the light ink.
  const onWash = (c: RGB, washOf: RGB, ratio = 4.6): RGB =>
    readableOn(c, washOf, contrast(ink, washOf) >= contrast(ground, washOf) ? ink : ground, ratio);
  const washes = (c: RGB): [RGB, RGB, RGB, RGB] =>
    dark
      // The strongest dark wash sits close to the state colour itself, so a
      // near-black label can read on it; a mid-tone reads with nothing.
      ? [mix(surface, c, 0.16), mix(surface, c, 0.26), mix(surface, c, 0.4), mix(surface, c, 0.85)]
      : [mix(surface, c, 0.07), mix(surface, c, 0.13), mix(surface, c, 0.22), mix(surface, c, 0.32)];

  const [blue1, blue2] = washes(accent);
  out["--surface-blue-1"] = rgb(blue1);
  out["--surface-blue-2"] = rgb(blue2);
  // A "solid" pill's label starts from whichever of white and near-black
  // reads on its fill, then is solved the rest of the way.
  out["--ink-blue-1"] = rgb(onWash(dark ? labelOn(blue2) : mix(accent, ink, 0.3), blue2));
  out["--ink-blue-2"] = rgb(onWash(dark ? mix(accent, ink, 0.2) : accent, blue2));
  out["--ink-blue-3"] = rgb(onWash(dark ? mix(accent, ink, 0.35) : strong(accent), surface));
  out["--ink-blue-4"] = rgb(onWash(dark ? mix(accent, ink, 0.55) : mix(accent, ink, 0.45), surface));
  out["--outline-blue-1"] = rgb(mix(surface, accent, 0.6));
  out["--outline-blue-2"] = rgb(accent);

  const [green1, green2] = washes(good);
  out["--surface-green-1"] = rgb(green1);
  out["--surface-green-2"] = rgb(green2);
  out["--surface-green-3"] = rgb(good);
  out["--ink-green-1"] = rgb(labelOn(good));
  out["--ink-green-2"] = rgb(onWash(dark ? mix(good, ink, 0.1) : good, surface));
  out["--ink-green-3"] = rgb(onWash(dark ? mix(good, ink, 0.4) : strong(good), green2));
  out["--outline-green-1"] = rgb(mix(surface, good, 0.6));
  out["--outline-green-2"] = rgb(good);

  const [amber1, amber2] = washes(warn);
  out["--surface-amber-1"] = rgb(amber1);
  out["--surface-amber-2"] = rgb(amber2);
  out["--ink-amber-1"] = rgb(onWash(dark ? labelOn(amber2) : mix(warn, ink, 0.35), amber2));
  out["--ink-amber-2"] = rgb(onWash(dark ? mix(warn, ink, 0.1) : warn, surface));
  out["--ink-amber-3"] = rgb(onWash(dark ? mix(warn, ink, 0.3) : strong(warn), amber1));
  out["--outline-amber-1"] = rgb(mix(surface, warn, 0.6));
  out["--outline-amber-2"] = rgb(warn);

  const [red1, red2, red3, red4] = washes(bad);
  out["--surface-red-1"] = rgb(red1);
  out["--surface-red-2"] = rgb(red2);
  out["--surface-red-3"] = rgb(red3);
  out["--surface-red-4"] = rgb(red4);
  out["--surface-red-5"] = rgb(bad);
  out["--surface-red-6"] = rgb(strong(bad));
  out["--ink-red-1"] = rgb(onWash(dark ? labelOn(red4) : mix(bad, ink, 0.35), red4));
  out["--ink-red-2"] = rgb(onWash(dark ? mix(bad, ink, 0.05) : mix(bad, surface, 0.15), surface, 3));
  out["--ink-red-3"] = rgb(onWash(dark ? mix(bad, ink, 0.2) : bad, surface));
  out["--ink-red-4"] = rgb(onWash(dark ? mix(bad, ink, 0.35) : strong(bad), red2));
  out["--outline-red-1"] = rgb(mix(surface, bad, 0.6));
  out["--outline-red-2"] = rgb(bad);
  out["--outline-red-3"] = rgb(strong(bad));
  out["--outline-red-4"] = rgb(bad);

  out["--surface-cyan-1"] = rgb(wash(info));
  out["--ink-cyan-1"] = rgb(info);
  out["--surface-orange-1"] = rgb(wash(warn));
  out["--outline-orange-1"] = rgb(warn);
  out["--surface-pink-1"] = rgb(wash(bad, 0.12));
  out["--ink-pink-1"] = rgb(mix(bad, accent, 0.4));
  out["--surface-violet-1"] = rgb(wash(accent, 0.18));
  out["--ink-violet-1"] = rgb(mix(accent, bad, 0.35));

  /* the console's own tokens (index.css), as channels */
  out["--c-app"] = channels(ground);
  out["--c-panel"] = channels(surface);
  out["--c-panel2"] = channels(surfaces[2]);
  out["--c-fg"] = channels(inks[9]);
  out["--c-fg2"] = channels(inks[8]);
  out["--c-fg3"] = channels(inks[6]);
  out["--c-fg4"] = channels(inks[5]);
  out["--c-fg5"] = channels(inks[4]);
  out["--c-arc"] = channels(accent);
  out["--c-alarm"] = channels(bad);
  out["--c-warn"] = channels(warn);
  out["--c-good"] = channels(good);
  out["--c-info"] = channels(info);
  const inkCh = channels(ink);
  out["--hairline"] = `rgb(${inkCh} / ${dark ? 0.08 : 0.11})`;
  out["--raise-sm"] = `rgb(${inkCh} / ${dark ? 0.03 : 0.025})`;
  out["--raise"] = `rgb(${inkCh} / ${dark ? 0.06 : 0.05})`;
  out["--raise-lg"] = `rgb(${inkCh} / ${dark ? 0.1 : 0.09})`;
  out["--sunken"] = dark ? "rgb(0 0 0 / 0.3)" : `rgb(${inkCh} / 0.06)`;
  out["--field-bg"] = `rgb(${inkCh} / ${dark ? 0.04 : 0.03})`;

  /* the shell: the body's tokens as `--sk-*`, the rail's as `--skc-*` and `--sk-f-*` */
  const body = shellTokens(main, dark);
  const rail = shellTokens(chrome, chromeDark);
  for (const [k, v] of Object.entries(body)) if (!k.startsWith("f-")) out[`--sk-${k}`] = v;
  for (const [k, v] of Object.entries(rail)) out[k.startsWith("f-") ? `--sk-${k}` : `--skc-${k}`] = v;
  out["--sk-on-accent"] = rgb(overrides.accent ? onAccent : labelOn(accent));

  /* the charts: magnitude on the accent, identity hues in a fixed order */
  const ramp = dark
    ? [mix(accent, ground, 0.55), mix(accent, ground, 0.3), accent, mix(accent, ink, 0.3), mix(accent, ink, 0.55)]
    : [mix(accent, surface, 0.55), mix(accent, surface, 0.25), accent, mix(accent, ink, 0.25), mix(accent, ink, 0.5)];
  ramp.forEach((c, i) => (out[`--chart-ramp-${i + 1}`] = rgb(c)));
  out["--chart-solid"] = rgb(dark ? mix(accent, ink, 0.2) : accent);
  const cat = [accent, bad, warn, good, mix(accent, bad, 0.5), mix(accent, good, 0.5), mix(warn, bad, 0.4), mix(bad, [255, 255, 255], 0.25), inks[4]];
  cat.forEach((c, i) => (out[`--chart-${i + 1}`] = rgb(dark ? mix(c, ink, 0.15) : c)));

  /* shape, type and rhythm */
  const radius = overrides.radius ?? spec.radius;
  const r = radius === "sharp" ? 0.5 : radius === "round" ? 1.5 : 1;
  // Corners never go past 10px, whatever the skin asks for.
  const rem = (v: number): string => `${Math.min(v * r, MAX_RADIUS_REM)}rem`;
  out["--radius-sm"] = rem(0.25);
  out["--radius"] = rem(0.5);
  out["--radius-md"] = rem(0.625);
  out["--radius-lg"] = rem(0.75);
  out["--radius-xl"] = rem(1);
  out["--radius-2xl"] = rem(1.25);

  const base = Math.min(MAX_BASE_SIZE, Math.max(MIN_BASE_SIZE, overrides.baseSize ?? spec.baseSize));
  out["--type-scale"] = String(Math.round((base / 14) * 1000) / 1000);
  out["--font-ui"] = fontStack(overrides.fontUi, spec.fonts.ui);
  out["--font-heading"] = fontStack(overrides.fontHeading, spec.fonts.heading);
  out["--font-mono"] = spec.fonts.mono;
  out["--density"] = (overrides.density ?? spec.density) === "compact" ? "0.85" : "1";

  return out;
}

/**
 * Desk, as shipped, is the standard: frappe-ui's own palette, type and
 * shape, and the shell's own colours, exactly as their stylesheets define
 * them. It is painted by writing nothing — every derived value is an
 * approximation of a stylesheet that is already right. Any other theme, or
 * Desk with a change on it, is painted in full.
 */
/** The largest corner anywhere in the app: 10px. */
const MAX_RADIUS_REM = 0.625;

export function variablesToPaint(value: Appearance, mode: Mode): Record<string, string> {
  if (value.theme === DEFAULT_THEME && Object.keys(value.overrides).length === 0) return {};
  return deriveVariables(presetById(value.theme), mode, value.overrides);
}

/* ---------------- a theme as a file ---------------- */

const FILE_VERSION = 1;

/** The kept look as a small JSON document a person can hand to a colleague. */
export function exportAppearance(value: Appearance): string {
  return JSON.stringify(
    { app: "go1-genie", kind: "appearance", version: FILE_VERSION, theme: value.theme, overrides: value.overrides },
    null,
    2
  );
}

/**
 * A theme file read back, kept to what the engine understands: a preset it
 * has, overrides in range. Anything else is refused with the reason, so a
 * file from a newer version fails plainly rather than half-applying.
 */
export function parseAppearanceFile(text: string): { ok: true; value: Appearance } | { ok: false; reason: string } {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, reason: "That file is not JSON." };
  }
  if (!raw || typeof raw !== "object") return { ok: false, reason: "That file does not describe a theme." };
  const doc = raw as Record<string, unknown>;
  if (doc.kind !== undefined && doc.kind !== "appearance") return { ok: false, reason: "That file is not a theme file." };
  if (typeof doc.version === "number" && doc.version > FILE_VERSION) {
    return { ok: false, reason: `That theme file is from a newer version (${doc.version}); this app reads version ${FILE_VERSION}.` };
  }
  if (typeof doc.theme !== "string" || !PRESETS.some((p) => p.id === doc.theme)) {
    return { ok: false, reason: `Unknown theme "${String(doc.theme ?? "")}". Themes here: ${PRESETS.map((p) => p.id).join(", ")}.` };
  }
  const o = doc.overrides && typeof doc.overrides === "object" ? (doc.overrides as Record<string, unknown>) : {};
  const overrides: AppearanceOverrides = {};
  if (o.accent !== undefined) {
    if (typeof o.accent !== "string" || !/^#[0-9a-fA-F]{6}$/.test(o.accent)) return { ok: false, reason: "The accent must be a six-digit hex colour." };
    overrides.accent = o.accent.toLowerCase();
  }
  for (const key of ["fontUi", "fontHeading"] as const) {
    if (o[key] !== undefined) {
      if (typeof o[key] !== "string" || !FONT_CHOICES.some((f) => f.id === o[key])) return { ok: false, reason: `Unknown font "${String(o[key])}".` };
      overrides[key] = o[key] as string;
    }
  }
  if (o.radius !== undefined) {
    if (o.radius !== "sharp" && o.radius !== "soft" && o.radius !== "round") return { ok: false, reason: "Corners must be sharp, soft or round." };
    overrides.radius = o.radius;
  }
  if (o.density !== undefined) {
    if (o.density !== "compact" && o.density !== "comfortable") return { ok: false, reason: "Density must be compact or comfortable." };
    overrides.density = o.density;
  }
  if (o.baseSize !== undefined) {
    const n = Number(o.baseSize);
    if (!Number.isFinite(n) || n < MIN_BASE_SIZE || n > MAX_BASE_SIZE) return { ok: false, reason: `Text size must be between ${MIN_BASE_SIZE} and ${MAX_BASE_SIZE}.` };
    overrides.baseSize = Math.round(n);
  }
  return { ok: true, value: { theme: doc.theme, overrides } };
}

/* ---------------- what must hold for every theme ---------------- */

export interface ContrastFailure {
  theme: string;
  mode: Mode;
  pair: string;
  ratio: number;
}

/** Text on every surface it sits on must pass AA; the accent must carry its label. */
export function contrastFailures(spec: ThemeSpec, mode: Mode): ContrastFailure[] {
  const v = deriveVariables(spec, mode);
  const c = (a: string, b: string) => contrast(hexToRgb(v[a]), hexToRgb(v[b]));
  const checks: [string, string, number][] = [
    ["--ink-gray-9", "--surface-white", 7],
    ["--ink-gray-8", "--surface-white", 4.5],
    ["--ink-gray-7", "--surface-white", 4.5],
    ["--ink-gray-6", "--surface-white", 4.5],
    ["--ink-gray-5", "--surface-white", 4.5],
    ["--ink-gray-9", "--surface-gray-1", 4.5],
    ["--ink-gray-9", "--surface-gray-2", 4.5],
    ["--ink-gray-8", "--surface-gray-3", 4.5],
    ["--ink-red-4", "--surface-white", 4.5],
    ["--ink-amber-3", "--surface-white", 4.5],
    ["--ink-green-3", "--surface-white", 4.5],
    ["--ink-blue-3", "--surface-white", 4.5],
  ];
  const out: ContrastFailure[] = [];
  for (const [a, b, min] of checks) {
    const ratio = c(a, b);
    if (ratio < min) out.push({ theme: spec.id, mode, pair: `${a} on ${b}`, ratio: Math.round(ratio * 100) / 100 });
  }
  // A button label is a UI component, held to WCAG's 3:1 for components —
  // the same bar frappe's own primary blue clears.
  const pairs: [string, string, number, string][] = [
    ["--ink-blue-2", "--surface-blue-2", 4.5, "blue pill"],
    ["--ink-green-3", "--surface-green-2", 4.5, "green pill"],
    ["--ink-amber-3", "--surface-amber-1", 4.5, "amber pill"],
    ["--ink-red-4", "--surface-red-2", 4.5, "red pill"],
    ["--ink-gray-6", "--surface-gray-2", 4.5, "grey pill"],
    ["--ink-blue-1", "--surface-blue-2", 4.5, "solid blue pill"],
    ["--ink-green-1", "--surface-green-3", 3, "solid green pill"],
    ["--ink-amber-1", "--surface-amber-2", 4.5, "solid amber pill"],
    ["--ink-red-1", "--surface-red-4", 4.5, "solid red pill"],
    ["--ink-white", "--surface-gray-7", 3, "solid grey pill"],
    ["--ink-blue-3", "--surface-white", 4.5, "blue text"],
    ["--ink-red-4", "--surface-white", 4.5, "red text"],
    ["--ink-amber-3", "--surface-white", 4.5, "amber text"],
    ["--ink-green-3", "--surface-white", 4.5, "green text"],
    ["--skc-on-accent", "--skc-blue", 3, "rail counter"],
    ["--sk-on-accent", "--sk-blue", 3, "accent button"],
  ];
  for (const [a, b, min, name] of pairs) {
    const ratio = contrast(hexToRgb(v[a]), hexToRgb(v[b]));
    if (ratio < min) out.push({ theme: spec.id, mode, pair: name, ratio: Math.round(ratio * 100) / 100 });
  }
  const railInk = contrast(hexToRgb(v["--sk-f-ink-8"]), hexToRgb(v["--sk-f-bg"]));
  if (railInk < 7) out.push({ theme: spec.id, mode, pair: "rail ink on rail", ratio: Math.round(railInk * 100) / 100 });
  const railMuted = contrast(hexToRgb(v["--sk-f-ink-6"]), hexToRgb(v["--sk-f-bg"]));
  if (railMuted < 4.5) out.push({ theme: spec.id, mode, pair: "rail muted ink on rail", ratio: Math.round(railMuted * 100) / 100 });
  const label = contrast(hexToRgb(v["--sk-blue"]), hexToRgb(v["--sk-on-accent"]));
  if (label < 3) out.push({ theme: spec.id, mode, pair: "label on accent", ratio: Math.round(label * 100) / 100 });
  return out;
}
